import { useNavigate } from "@tanstack/react-router";

import { Link } from "@/components/link";
import { FeaturePageLayout } from "@/features/shared/layout";

import { categoryLabel } from "../category-labels";
import { GuidelineAssignment } from "../GuidelineAssignment";
import { ReadOnlyField } from "../ReadOnlyField";
import { useMbcAdminData } from "../useMbcAdminData";

export function SubCategoryPage({
  subCategoryId,
}: {
  readonly subCategoryId: number;
}) {
  const navigate = useNavigate();
  const { assignments, catalog, error, guidelines, isLoading } =
    useMbcAdminData();

  const subCategory = catalog?.subCategories.find(
    ({ id }) => id === subCategoryId,
  );

  return (
    <div className="p-xl">
      <FeaturePageLayout
        backButton
        isLoading={isLoading}
        title="Edit subcategory"
      >
        <div className="space-y-8 rounded-b-xl bg-bg-primary px-6 py-8">
          {error && (
            <p className="text-sm text-text-error" role="alert">
              Could not load the subcategory: {error.message}
            </p>
          )}
          {!subCategory && !error && (
            <p className="text-sm text-text-tertiary">Subcategory not found</p>
          )}
          {subCategory && assignments && guidelines && (
            <>
              <dl className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <ReadOnlyField label="Name">{subCategory.name}</ReadOnlyField>
                <ReadOnlyField label="Category">
                  {categoryLabel(subCategory.category)}
                </ReadOnlyField>
                <ReadOnlyField label="Design types">
                  {subCategory.designTypes.length === 0
                    ? "—"
                    : subCategory.designTypes.map((designType, index) => (
                        <span key={designType.id}>
                          {index > 0 && ", "}
                          <Link
                            className="text-sm"
                            href={`/admin/design-types/${designType.id}`}
                          >
                            {designType.name}
                          </Link>
                        </span>
                      ))}
                </ReadOnlyField>
              </dl>

              <GuidelineAssignment
                assignments={assignments}
                guidelines={guidelines}
                onClose={() => navigate({ to: "/admin/subcategories" })}
                scope={{
                  kind: "subCategory",
                  name: subCategory.name,
                  subCategoryId: subCategory.id,
                }}
              />
            </>
          )}
        </div>
      </FeaturePageLayout>
    </div>
  );
}
